import React, { useCallback, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import { getBrandImageUrl, getStoreLocation } from 'ck-utils';
import { Icon, LazyLoadImage } from 'ck-ui';
import { trackCustomEventThenRedirect } from 'ck-utils/algoliaAnalytics';
import { sanityClient, getSanityImg, getSanityRegion, sanityToImgix } from 'src/sanityClient';
import styles from './Mobilemenu.module.scss';

const MENS = 'Mens';
const WOMENS = 'Womens';
const KIDS = 'Kids';
const SALE = 'Sale';

const tabNames = process.env.REACT_APP_STORE === 'culture-kings-us.myshopify.com' ? [MENS, WOMENS, SALE] : [MENS, WOMENS, KIDS, SALE];

export const Mobilemenu = ({ isLoggedIn, footerLinks, mobileMenu }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSecondaryMenu, setActiveSecondaryMenu] = useState(-1);
  const [menuMounted, setMenuMounted] = useState(false);
  const tablistRef = useRef(null);
  const storeLocation = getStoreLocation();

  useEffect(() => {
    const tablist = tablistRef.current;
    const tabs = [...tablist.childNodes];
    const newActiveTab = tabs[activeIndex];
    if (newActiveTab) {
      newActiveTab.focus();
    }
  }, [activeIndex]);

  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return;
    window.theme.initMobileMenu();
    window.theme.initRegionChange();
  }, []);

  /**
   * Handle keyboard accessibility
   * https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-19
   */
  const handleKeyDown = useCallback(
    (event) => {
      const tablist = tablistRef.current;
      const tabs = [...tablist.childNodes];

      let newActiveIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newActiveIndex = activeIndex === 0 ? tabs.length - 1 : activeIndex - 1;
          break;
        case 'ArrowRight':
          event.preventDefault();
          newActiveIndex = activeIndex === tabs.length - 1 ? 0 : activeIndex + 1;
          break;
        case 'Home':
          event.preventDefault();
          newActiveIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newActiveIndex = tabs.length - 1;
          break;
        default:
          break;
      }
      if (newActiveIndex !== undefined) {
        setActiveIndex(newActiveIndex);
      }
    },
    [activeIndex]
  );

  const handleClick = (_event, index) => {
    setActiveIndex(index);
  };

  const trackLinkClick = (url, label) => e => {
    e.preventDefault();
    const currentTabName = tabNames[activeIndex];
    const currentSecondaryMenuName = mobileMenu?.[currentTabName]?.items?.[activeSecondaryMenu]?.item?.title;
    trackCustomEventThenRedirect({
      url,
      event: 'gaEvent',
      data: {
        gaEvent: {
          name: 'menu_click',
          nonInteractionHit: false,
          category: 'Hamburger Menu',
          action: 'Click',
          label: `${currentTabName.toUpperCase()} / ${currentSecondaryMenuName ? `${currentSecondaryMenuName.toUpperCase()} / ${label}` : label}`
        }
      }
    });
  };

  const renderImageGrid = (items, title, buttonText, buttonLink) => items?.length > 0 && (
    <>
      <h6 className={classnames(styles.subheading, 'heading7')}>{title}</h6>
      <div className={styles.grid}>
        {items.map((item) => (
          <a
            key={item.title}
            className={item.image ? styles.imageBlock : styles.brand}
            href={item.href}
            title={item.title}
            onClick={trackLinkClick(item.href, item.title)}
          >
            {item.image ? (
              <LazyLoadImage
                src={sanityToImgix(getSanityImg(item.image)
                  .auto('format')
                  .height(100)
                  .url())}
                className={styles.center}
                alt={item.title}
              />
            ) : (
              <LazyLoadImage
                src={getBrandImageUrl(item.title, 'centre')}
                alt={`${item.title} logo`}
              />
            )}
          </a>
        ))}
        <a href={buttonLink} className="button primary fullwidth-all">
          {buttonText}
        </a>
      </div>
    </>
  );

  const renderSubmenu = (title, tabName, { items, vendors, teams }) => (
    <>
      <div className={classnames(styles.submenuHeading, 'is-uppercase heading7')}>
        <button
          type="button"
          onClick={() => setActiveSecondaryMenu(-1)}
          aria-label={`Back to previous menu - ${tabName}`}
        >
          <Icon name="arrow-left" />
        </button>
        <span>{tabName} / {title}</span>
      </div>
      <div className={styles.scrollable}>
        <h6 className={classnames(styles.subheading, 'heading8')}>SHOP BY PRODUCT</h6>
        <ul className={styles.submenuList}>
          {items.map((item) => (
            <li key={item.href} className={styles.submenuItem}>
              <a href={item.href} onClick={trackLinkClick(item.href, item.title)}>
                <div>
                  <div>
                    <LazyLoadImage
                      src={sanityToImgix(getSanityImg(item.image)
                        .width(100)
                        .auto('format')
                        .url())}
                      role="presentation"
                      alt={item.title}
                    />
                  </div>
                </div>
                <span>{item.title}</span>
                <Icon name="arrow-right" />
              </a>
            </li>
          ))}
        </ul>
        {renderImageGrid(vendors, 'FEATURED BRANDS', 'VIEW ALL BRANDS', storeLocation !== 'US' ? '/pages/featured-brands' : '/collections/brands')}
        {renderImageGrid(teams, 'FEATURED TEAMS', 'SHOP ALL TEAMS', '/page/sports')}
      </div>
    </>
  );

  const renderCountrySelector = () => {
    switch (storeLocation) {
      case 'NZ':
        return <>New Zealand &middot; $ (NZD)</>;
      case 'US':
        return <>United States &middot; $ (USD)</>;
      default:
        return <>Australia &middot; $ (AUD)</>;
    }
  };

  return (
    <div className={styles.root}>
      {/**
       * Not valid in this case because this is an automatic tablist where each
       * item is automatically activated when they receive focus:
       * https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
       *
       */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div className={styles.tablist} role="tablist" aria-label="Mens / Womens / Kids / Sale" ref={tablistRef}>
        {tabNames.map((title, index) => (
          <button
            type="button"
            key={title}
            className={classnames(styles.tab, {
              [styles.activeTab]: index === activeIndex,
            })}
            role="tab"
            aria-selected={index === activeIndex}
            aria-controls={`${title.toLowerCase()}-tabpanel`}
            id={`${title.toLowerCase()}-tab`}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={(event) => {
              handleClick(event, index);
            }}
            onKeyDown={handleKeyDown}
          >
            <span>{title}</span>
          </button>
        ))}
      </div>
      <div className={styles.scrollable}>
        <div className="search-bar-wrapper">
          <div className="ais-SearchBox">
            <form className="ais-SearchBox-form">
              <input
                type="search"
                placeholder="Search brands or products"
                className="ais-SearchBox-input js-search-bar-trigger"
              />
              <button
                type="button"
                title="Submit"
                className="ais-SearchBox-submit js-search-bar-trigger"
              >
                <span
                  className="ais-SearchBox-submitIcon icon icon-search size-regular secondary"
                />
              </button>
            </form>
          </div>
        </div>
        {tabNames.map((title, index) => (
          <div
            key={title}
            className={classnames(styles.tabpanel, {
              [styles.activeTabpanel]: index === activeIndex,
            })}
            role="tabpanel"
            id={`${title.toLowerCase()}-tabpanel`}
            aria-labelledby={`${title.toLowerCase()}-tab`}
          >
            <nav aria-label={`Shop ${title}`}>
              <ul className={styles.itemList}>
                {mobileMenu?.[title]?.items?.map(({ item, submenu }, itemIndex) => {
                  let node;
                  if (submenu?.items?.length > 0) {
                    node = (
                      <button
                        onClick={() => {
                          setActiveSecondaryMenu(itemIndex);
                        }}
                        type="button"
                        aria-label={`Open ${item.title.toLowerCase()} submenu`}
                      >
                        {item.title}
                      </button>
                    );
                  } else {
                    node = <a href={item.href} onClick={trackLinkClick(item.href, item.title)}>{item.title}</a>;
                  }

                  return (
                    <li key={item.title || item.href}>
                      <div className={styles.itemLink} style={{ color: item.color }}>
                        <LazyLoadImage
                          src={sanityToImgix(getSanityImg(item.image)
                            .width(600)
                            .auto('format')
                            .url())}
                          alt={item.title || item.href?.split('/').pop()}
                        />
                        {node}
                      </div>
                      {submenu?.items?.length > 0 && (
                        <Transition
                          onEnter={() => {
                            requestAnimationFrame(() => {
                              setMenuMounted(true);
                            });
                          }}
                          onExited={() => {
                            requestAnimationFrame(() => {
                              setMenuMounted(false);
                            });
                          }}
                          timeout={300}
                          in={activeSecondaryMenu === itemIndex}
                        >
                          {(state) => (
                            <div
                              style={{
                                display: state !== 'exited' ? 'block' : 'none',
                                transform: ['entered', 'entering'].includes(state) && menuMounted ? 'translate3d(0, 0, 0)' : undefined,
                              }}
                              className={styles.submenuWrapper}
                            >
                              {renderSubmenu(item.title, title, submenu)}
                            </div>
                          )}
                        </Transition>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
            {renderImageGrid(mobileMenu?.[title]?.vendors, 'FEATURED BRANDS', 'VIEW ALL BRANDS', storeLocation !== 'US' ? '/pages/featured-brands' : '/collections/brands')}
            {renderImageGrid(mobileMenu?.[title]?.teams, 'FEATURED TEAMS', 'SHOP ALL TEAMS', '/page/sports')}
          </div>
        ))}
        <nav aria-label="Footer links" className={styles.footer}>
          <ul>
            {isLoggedIn ? (
              <>
                <li className={styles.footerItem}>
                  <a href="/account">
                    <Icon name="user" />
                    <span>Account</span>
                  </a>
                </li>
                <li className={styles.footerItem}>
                  <a href="/account/logout">
                    <Icon name="sign-in" />
                    <span>Sign Out</span>
                  </a>
                </li>
              </>
            ) : (
              <>
                <li className={styles.footerItem}>
                  <a href="/account/login">
                    <Icon name="user" />
                    <span>Sign In</span>
                  </a>
                </li>
                <li className={styles.footerItem}>
                  <a href="/account/register">
                    <Icon name="register" />
                    <span>Register</span>
                  </a>
                </li>
              </>
            )}
            <li className={styles.footerItem}>
              <a href="#" className="header-region js-change-region">
                <Icon name="world" />
                <span>{renderCountrySelector()}</span>
              </a>
            </li>
            {footerLinks.map(({ title, url, children }) => {
              let iconClassname;

              if (url.includes('card')) {
                iconClassname = 'gift-card';
              } else if (url.includes('grid')) {
                iconClassname = 'pants';
              } else if (url.includes('store')) {
                iconClassname = 'location';
              } else if (url.includes('help')) {
                iconClassname = 'question';
              } else {
                iconClassname = 'info';
              }

              const icon = <Icon name={iconClassname} />;

              return (
                <li className={styles.footerItem} key={title}>
                  {children.length > 0 ? (
                    <details>
                      <summary>
                        <div>
                          {icon}
                          <span>{title}</span>
                          <span className="icon" />
                        </div>
                      </summary>
                      <ul>
                        {children.map((child) => (
                          <li key={child.title}>
                            <a href={child.url}>{child.title}</a>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : (
                    <a href={url} tabIndex={0}>
                      {icon}
                      <span>{title}</span>
                    </a>
                  )}
                </li>
              );
            })}
            {window.OneTrust && (
              <li className={styles.footerItem}>
                <a>
                  <Icon name="info" />
                  <span id="ot-sdk-btn" className="ot-sdk-show-settings">Cookie Settings</span>
                </a>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </div>
  );
};

Mobilemenu.propTypes = {
  isLoggedIn: PropTypes.bool,
  footerLinks: PropTypes.arrayOf(PropTypes.object),
  mobileMenu: PropTypes.object
};

Mobilemenu.defaultProps = {
  isLoggedIn: false,
  footerLinks: [],
  mobileMenu: {}
};

export const getStaticProps = async () => {
  const region = getSanityRegion();

  const fragment = `{
    "vendors": vendors[]{
      "title": vendor->title,
      href
    },
    "teams": teams[]{
      "title": team->title,
      "image": team->teamImage,
      href
    },
    "items": items[]{
      "item": item->{
        title, color, image, href
      },
      "submenu": {
        "items": submenu.items[]->{
          title, image, href
        },
        "vendors": submenu.vendors[]{
          "title": vendor->title,
          href
        },
        "teams": submenu.teams[]{
          "title": team->title,
          "image": team->teamImage,
          href
        },
      }
    }
  }`;

  const mobileMenu = await sanityClient.fetch(`{
    "${MENS}": *[ _id == "${region}-mensMobileMenu"]${fragment}[0],
    "${WOMENS}": *[ _id == "${region}-womensMobileMenu"]${fragment}[0],
    "${KIDS}": *[ _id == "${region}-kidsMobileMenu"]${fragment}[0],
    "${SALE}": *[ _id == "${region}-salesMobileMenu"]${fragment}[0]
  }`);

  return {
    mobileMenu
  };
};
